import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useMediaQuery } from '@/lib/useMediaQuery';

/**
 * A centered dialog on desktop, a drag-dismissable bottom sheet on phones.
 *
 * Built on the native <dialog> rather than a portal-and-listeners implementation:
 * focus trapping, Escape, inertness of the page behind, and the top layer all come for
 * free and correct. Hand-rolled versions of those are where modal accessibility
 * usually goes wrong.
 */

/**
 * Half the sheet, measured at the start of each drag rather than a fixed pixel count.
 *
 * 96px was a fifth of the sheet on a phone and it fired constantly — a scroll that
 * started slightly off, a thumb resettling, and the card was gone. Proportional also
 * means the gesture feels the same on a small phone and a tablet.
 */
const DISMISS_FRACTION = 0.5;

/**
 * A deliberate flick still counts, but it has to be both fast AND have travelled a
 * third of the way. Velocity alone is what made this twitchy: any quick touch
 * registers a high px/ms over a very short distance.
 */
const FLICK_VELOCITY = 1.2;
const FLICK_FRACTION = 0.33;

export function ResponsiveSheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode | undefined;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const drag = useRef<{ startY: number; startedAt: number; dy: number; height: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  /**
   * Drag is a bottom-sheet gesture and belongs only where the sheet is one.
   *
   * The handlers were attached at every width. The grab handle is hidden on desktop,
   * but the HEADER kept them — so a mouse-drag on the title bar slid a centred dialog
   * off the bottom of the screen, which is not a gesture anyone was reaching for.
   */
  const isSheet = !useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Fires for Escape as well as close(), so both paths stay in sync with React state.
    const handleClose = () => {
      dialog.style.removeProperty('translate');
      setDragging(false);
      drag.current = null;
      onClose();
    };
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  /**
   * Sees the drag through to the bottom before closing.
   *
   * Calling close() from where the finger left off would snap the sheet back to its
   * open position for one frame and then play the exit from there. Driving it to 100%
   * first means the release continues the gesture instead of interrupting it.
   */
  const dismiss = useCallback(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Desktop has nowhere to slide to — close() and let the CSS fade handle it.
    if (!isSheet) {
      dialog.close();
      return;
    }

    setDragging(false);
    dialog.style.setProperty('translate', '0 100%');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      dialog.close();
    };
    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName === 'translate') finish();
    };

    dialog.addEventListener('transitionend', onEnd);
    // Fallback, because a transition that never starts never ends — reduced motion
    // collapses the duration to ~0 and the event can be missed entirely.
    window.setTimeout(finish, 320);
  }, [isSheet]);

  function onPointerDown(event: PointerEvent<HTMLElement>) {
    drag.current = {
      startY: event.clientY,
      startedAt: event.timeStamp,
      dy: 0,
      // Measured per drag: the sheet is a vh, so it changes with the browser chrome.
      height: ref.current?.getBoundingClientRect().height ?? window.innerHeight,
    };
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent<HTMLElement>) {
    const state = drag.current;
    const dialog = ref.current;
    if (!state || !dialog) return;

    // Downward only. Dragging up would lift the sheet off the bottom edge and reveal
    // the page behind it, which reads as a rendering fault rather than a gesture.
    state.dy = Math.max(0, event.clientY - state.startY);
    dialog.style.setProperty('translate', `0 ${state.dy}px`);
  }

  function onPointerUp(event: PointerEvent<HTMLElement>) {
    const state = drag.current;
    const dialog = ref.current;
    drag.current = null;
    if (!state || !dialog) return;

    const velocity = state.dy / Math.max(1, event.timeStamp - state.startedAt);
    const dragged = state.dy > state.height * DISMISS_FRACTION;
    const flicked = velocity > FLICK_VELOCITY && state.dy > state.height * FLICK_FRACTION;

    if (dragged || flicked) {
      dismiss();
      return;
    }

    // Under the threshold: hand it back to CSS, which springs it home.
    setDragging(false);
    dialog.style.removeProperty('translate');
  }

  return (
    <dialog
      ref={ref}
      aria-label={title}
      onClick={(event) => {
        // Clicking the backdrop closes. The backdrop is the dialog element itself,
        // so this only fires when the click missed the content box.
        if (event.target === ref.current) dismiss();
      }}
      className={`
        sheet m-0 max-h-full w-full max-w-full border-0 bg-transparent p-0
        mt-auto
        md:m-auto md:w-[min(56rem,92vw)] md:max-w-none
        ${dragging ? 'sheet-dragging' : ''}
      `}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        /*
          56rem, capped at 92vw. max-w-lg was 32rem — fine for a confirm dialog, far too
          narrow for a card whose body is markdown with a five-column table in it: the
          table scrolled sideways inside a box a third of the screen wide while the rest
          of the monitor sat empty.
        */
        className="
          flex h-[85vh] w-full flex-col overflow-hidden rounded-t-2xl border
          md:h-[82vh] md:rounded-2xl
        "
        style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
      >
        {/*
          The grab handle IS the close control on a phone, so it is a real button —
          keyboard and screen-reader users get the same affordance without a gesture.
          touch-action: none stops the browser scrolling the page mid-drag.
        */}
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="flex w-full shrink-0 cursor-grab touch-none justify-center pb-1 pt-2.5 active:cursor-grabbing md:hidden"
        >
          <span
            aria-hidden
            className="h-1 w-9 rounded-full"
            style={{ background: 'var(--border-strong)' }}
          />
        </button>

        <header
          // The whole header drags, not just the 36px handle — a bar you can miss is a
          // gesture people give up on.
          {...(isSheet
            ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }
            : {})}
          className="flex shrink-0 touch-none items-start justify-between gap-3 border-b px-4 pb-3 pt-2 md:touch-auto md:py-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <h2 className="text-sm font-semibold leading-snug">{title}</h2>

          {/* Desktop keeps the ×: a centred dialog has no edge to drag toward. */}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="-m-2 hidden size-11 shrink-0 items-center justify-center rounded-lg md:flex"
            style={{ color: 'var(--text-muted)' }}
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto no-scrollbar px-4 py-4">{children}</div>

        {footer && (
          <footer
            className="shrink-0 border-t px-4 py-3"
            style={{ borderColor: 'var(--border)', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  );
}
